import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import GameContentProvider from '../providers/GameContentProvider';
import PaymentProvider from '../payment/PaymentProvider';
import './GameDetailsView.css';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsLoggedIn } from '../auth/authSlice';
import { useTranslation } from 'react-i18next';
import { FaLightbulb, FaMapSigns, FaClock, FaUsers } from 'react-icons/fa';

// 1. IMPORT YOUR GLOBAL CONFIG
import { IS_TESTING_CAMPAIGN } from '../config'; // Adjust path if needed

const GameDetailsView = () => {
    const { t, i18n } = useTranslation();
    const { title } = useParams();
    const [game, setGame] = useState(null);
    const [showOverlay, setShowOverlay] = useState(false);
    const navigate = useNavigate();
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const [referralPoints, setReferralPoints] = useState(null);
    const [mail, setEmail] = useState(null);
    const [type, setType] = useState(0);
    const [overlayError, setOverlayError] = useState('');
    const [finalPrice, setFinalPrice] = useState(null);
    const [discountApplied, setDiscountApplied] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const gameContentProvider = new GameContentProvider();
                const gameDetails = await gameContentProvider.getGameDetails(title, (i18n.language || 'bg').substring(0, 2));
                setGame(gameDetails);

                if (isLoggedIn) {
                    const token = localStorage.getItem("token");
                    const points = await gameContentProvider.getReferralPoints(token);
                    setReferralPoints(points);
                    console.log("referralPoints: " + points);
                }
            } catch (error) {
                console.error('Error fetching game details:', error);
            }
        };

        fetchData();
    }, [title, isLoggedIn, i18n.language]);

    useEffect(() => {
        const validateLocalDiscount = async () => {
            const savedCode = localStorage.getItem('applied_discount_code');
            if (savedCode && savedCode.trim() && game) {
                try {
                    const response = await fetch('/api/validate-discount', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ code: savedCode.trim(), gameId: game.id }),
                    });
                    const data = await response.json();
                    if (data.valid) {
                        const base = Number(game.reducedPrice);
                        const reduction = (base * data.reduction_percent) / 100;
                        setFinalPrice(Math.max(0, base - reduction).toFixed(2));
                        setDiscountApplied(true);
                    }
                } catch (error) {
                    console.error('Error validating local discount:', error);
                }
            }
        };

        validateLocalDiscount();
    }, [game]);

    const handleBuyGiftVoucher = async () => {
        if (!isLoggedIn) {
            setType(1);
            setShowOverlay(true);
        } else {
            const data = {
                name: game.title,
                price: Number(game.reducedPrice).toFixed(2),
                gameId: game.id,
            };
            navigate('/get-gift_voucher', { state: data });
        }
    };

    const handleBuyCoupon = async () => {
        if (!isLoggedIn) {
            setType(0);
            setShowOverlay(true);
        } else {
            const provider = new PaymentProvider();
            const token = localStorage.getItem('token');
            console.log("token: " + token);
            if (IS_TESTING_CAMPAIGN) {
                if (token) {
                    const data = await provider.createCoupon(token, game.id, 0);
                    if (data) {
                        navigate('/success', { state: data, replace: true });
                    } else {
                        console.log("Problem creating new coupon");
                    }
                }
            }
            else if (referralPoints !== undefined && referralPoints >= game.reducedPrice) {

                if (token) {
                    const data = await provider.createCoupon(token, game.id, 0);
                    if (data) {
                        const result = await provider.decreaseReferalPoints(token, referralPoints);

                        if (result) {
                            navigate('/success', { state: data, replace: true });
                        } else {
                            console.log("Problem decreasing referral points");
                        }
                    } else {
                        console.log("Problem creating new coupon");
                    }
                } else {
                    console.log("Problem getting token");
                }
            } else {
                const data = {
                    name: game.title,
                    price: Number(game.reducedPrice).toFixed(2),
                    gameId: game.id,
                };
                navigate('/get-coupon', { state: data });
            }
        }
    };

    const handleLogin = () => {
        setShowOverlay(false);
        navigate('/login');
    };

    const handleProceedPayment = (mail) => {
        const data = {
            name: game.title,
            price: Number(game.reducedPrice).toFixed(2),
            gameId: game.id,
            mail: mail,
        };

        if (type === 0) {
            navigate('/get-coupon', { state: data });
        } else {
            navigate('/get-gift_voucher', { state: data });
        }
    };

    const handleCloseOverlay = () => {
        setShowOverlay(false);
    };

    return (
        <div className="container">
            {game ? (
                <div className="game-details">
                    <h2 className="title">{game.title}</h2>
                    <div className="content">
                        <img className="image" src={game.imageUrl} alt="" />

                        <div className="game-meta">
                            <div className="meta-row">
                                <span className="meta-label">{t('game_difficulty') || 'Game Difficulty'}:</span>
                                <div className="difficulty">
                                    {[1, 2, 3].map((i) => (
                                        <span key={i} className={`flame ${i <= game.level ? 'active' : 'inactive'}`}>
                                            🔥
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div className="meta-row">
                                <span className="meta-label">{t('game_start_location') || 'Game Start Location'}:</span>
                                <div className="start-location">
                                    <a
                                        href={`https://www.google.com/maps/search/?api=1&query=$${game.start_location_lat},${game.start_location_lon}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {game.start_location}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="description-container">
                            <section className="description-section">
                                <h3><FaLightbulb className="icon" /> {t('game_intro')}</h3>
                                <p>{game.description_intro.split('\n').map((line, i) => <span key={i}>{line}<br /></span>)}</p>
                            </section>

                            <section className="description-section">
                                <h3><FaMapSigns className="icon" /> {t('how_it_works')}</h3>
                                <ul>
                                    {game.description_how.split('\n').map((line, i) => (
                                        <li key={i}>{line}</li>
                                    ))}
                                </ul>
                            </section>

                            <section className="description-section">
                                <h3><FaClock className="icon" /> {t('time_accessibility')}</h3>
                                <ul>
                                    {game.description_time.split('\n').map((line, i) => (
                                        <li key={i}>{line}</li>
                                    ))}
                                </ul>
                            </section>

                            <section className="description-section">
                                <h3><FaUsers className="icon" /> {t('who_can_play')}</h3>
                                <ul>
                                    {game.description_who.split('\n').map((line, i) => (
                                        <li key={i}>{line}</li>
                                    ))}
                                </ul>
                            </section>
                        </div>

                        <div className="video-container">
                            <video className="responsive-video" controls>
                                <source src={"/assets/videos/" + game.video + "_" + i18n.language + ".mp4"} type="video/mp4" />
                            </video>
                        </div>

                        {/* ----------------------------------------- */}
                        {/* 2. UPDATE PRICING LOGIC */}
                        {/* ----------------------------------------- */}
                        {IS_TESTING_CAMPAIGN ? (
                            <p className="game-price campaign-free" style={{ color: '#ff6347', fontWeight: 'bold' }}>
                                {t('free')}
                            </p>
                        ) : (
                            // Original pricing logic is moved inside the 'else' block
                            <>
                                {referralPoints !== null && referralPoints > game.price && game.reducedPrice !== game.price && (
                                    <p className="game-price">
                                        {t('free')}
                                    </p>
                                )}

                                {(!referralPoints || referralPoints < game.reducedPrice) && game.reducedPrice === game.price && !discountApplied && (
                                    <p className="game-price">
                                        <span className="price">{Number(game.price).toFixed(2)}</span>
                                        <span className="currency">{game.currency.toUpperCase()}</span>
                                    </p>
                                )}

                                {(!referralPoints || referralPoints < game.reducedPrice) && (game.reducedPrice < game.price || discountApplied) && (
                                    <div className="game-prices">
                                        <p className="game-regular-price" style={{ textDecoration: 'line-through' }}>
                                            <span className="price">{Number(discountApplied ? game.reducedPrice : game.price).toFixed(2)}</span>
                                            <span className="currency">{game.currency.toUpperCase()}</span>
                                        </p>
                                        <p className="game-reduced-price">
                                            <span className="price">{discountApplied ? finalPrice : Number(game.reducedPrice).toFixed(2)}</span>
                                            <span className="currency">{game.currency.toUpperCase()}</span>
                                        </p>
                                        {discountApplied && (
                                            <p style={{ color: '#4CAF50', fontSize: '0.9em', marginTop: '5px', fontWeight: 'bold' }}>
                                                {t('discount_applied', { defaultValue: 'Discount code applied!' })}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                        {/* ----------------------------------------- */}

                        {/* ----------------------------------------- */}
                        {/* 3. UPDATE BUTTON LOGIC */}
                        {/* ----------------------------------------- */}
                        <div className='butoons'>
                            <button className="button" onClick={() => handleBuyCoupon()}>
                                {t('get_a_coupon')}
                            </button>
                            {/* Hide gift voucher if campaign is active */}
                            {/* {!IS_TESTING_CAMPAIGN && (
                                <button className="button" onClick={() => handleBuyGiftVoucher()}>
                                    {t('gift_voucher')}
                                </button>
                            )} */}
                        </div>
                        {/* ----------------------------------------- */}

                    </div>
                </div>
            ) : (
                <div className="loading">{t('loading')}...</div>
            )}

            {/* Overlay view */}
            {showOverlay && (
                <div className="overlay">
                    <div className="overlay-content">
                        <h2>{t('not_loged')}</h2>
                        <p>{t('you_must_login')}</p>

                        <div className="guest-checkout-container" style={{ margin: '20px 0' }}>
                            <p style={{ fontSize: '14px', marginBottom: '10px', color: '#555' }}>
                                {t('enter_email_to_proceed')}
                            </p>
                            <input
                                type="email"
                                placeholder={t('email')}
                                value={mail || ''}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setOverlayError('');
                                }}
                                className="input-field"
                                style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    borderRadius: '8px',
                                    border: '1px solid #ccc',
                                    boxSizing: 'border-box',
                                    fontSize: '15px'
                                }}
                            />
                            {overlayError && (
                                <p style={{ color: '#dc3545', fontSize: '13px', marginTop: '6px', textAlign: 'left' }}>
                                    {overlayError}
                                </p>
                            )}
                        </div>

                        <div className="buttons" style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                            <button
                                className="button"
                                style={{ width: '100%', backgroundColor: '#28a745', fontWeight: 'bold' }}
                                onClick={() => {
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                    if (!mail || !emailRegex.test(mail ? mail.trim() : '')) {
                                        setOverlayError(t('email_format_error'));
                                        return;
                                    }
                                    handleProceedPayment(mail.trim());
                                }}
                            >
                                {t('proceed_to_payment')}
                            </button>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '5px' }}>
                                <button className="button" style={{ flex: 1, backgroundColor: '#007bff' }} onClick={handleLogin}>
                                    {t('login')}
                                </button>
                                <button className="button" style={{ flex: 1, backgroundColor: '#6c757d' }} onClick={handleCloseOverlay}>
                                    {t('close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GameDetailsView;
